import React, { useState, useEffect } from 'react';
import { radiologyTATService, TATSummaryRadiology } from '../../lib/services.radiology';
import { Clock, TrendingUp, TrendingDown } from 'lucide-react';
import toast from 'react-hot-toast';

export default function RadiologyTATDashboard() {
  const [summary, setSummary] = useState<TATSummaryRadiology[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterModality, setFilterModality] = useState<string>('all');

  useEffect(() => {
    loadSummary();
  }, [filterModality]);

  const loadSummary = async () => {
    try {
      const filters = filterModality !== 'all' ? { modality: filterModality } : undefined;
      const data = await radiologyTATService.getSummary(filters);
      setSummary(data);
    } catch (error) {
      console.error('Failed to load TAT summary:', error);
      toast.error('Failed to load TAT data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">TAT Dashboard</h1>
        <p className="text-gray-600">Turnaround time metrics for radiology</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex gap-4 items-center">
          <label className="text-sm font-medium text-gray-700">Filter by Modality:</label>
          <select
            value={filterModality}
            onChange={(e) => setFilterModality(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="all">All Modalities</option>
            <option value="XR">X-Ray</option>
            <option value="USG">Ultrasound</option>
            <option value="CT">CT Scan</option>
            <option value="MRI">MRI</option>
            <option value="MAMMO">Mammography</option>
            <option value="DXA">DEXA</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modality</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Orders</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg TAT</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min TAT</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max TAT</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority Dist.</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summary.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {item.modality}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{item.test_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.total_orders}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Math.floor(item.avg_tat_minutes / 60)}h {Math.floor(item.avg_tat_minutes % 60)}m
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {Math.floor(item.min_tat_minutes / 60)}h {Math.floor(item.min_tat_minutes % 60)}m
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {Math.floor(item.max_tat_minutes / 60)}h {Math.floor(item.max_tat_minutes % 60)}m
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div className="flex gap-2">
                      <span className="text-red-600">S:{item.stat_count}</span>
                      <span className="text-orange-600">U:{item.urgent_count}</span>
                      <span className="text-green-600">R:{item.routine_count}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {summary.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No TAT data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
